// Dossier 02 — Je découvre le deux-roues motorisé (Code2Roues.fr × CodeVoiture.fr)
const IMG = {
  equip: 'https://static.prod-images.emergentagent.com/jobs/c292cde0-62e6-4d67-8d5e-ce29527d25c3/images/ed86d7ae6b177edb948f91ec208c03fb30e4d3a08b93a3cf27488289ed6c8da9.jpeg',
  commandes: 'https://static.prod-images.emergentagent.com/jobs/c292cde0-62e6-4d67-8d5e-ce29527d25c3/images/e1a34c14d4acb8e3ea0f43836ac57021ccb04baf858814724faf91d9eadb13d3.jpeg',
  controle: 'https://static.prod-images.emergentagent.com/jobs/c292cde0-62e6-4d67-8d5e-ce29527d25c3/images/2cbe092d16227847842c790b219352cab941f49add2c8e10ff011703545a1852.jpeg',
  equilibre: 'https://static.prod-images.emergentagent.com/jobs/c292cde0-62e6-4d67-8d5e-ce29527d25c3/images/3111d5d28cd951c68d2a2bb68310ca4f7a47ba19489a80a08e77146fb1987168.jpeg',
  risques: 'https://static.prod-images.emergentagent.com/jobs/c292cde0-62e6-4d67-8d5e-ce29527d25c3/images/7f80a0b8a158eea29a552209dd201c1648fce9d3c8b86797556b2d119d4e47bb.jpeg',
};

export const DOSSIER_02 = {
  id: 'deux-roues',
  num: 'Dossier 02',
  title: 'Je découvre le deux-roues motorisé',
  desc: "Équipement, commandes, contrôles, équilibre, regard, freinage et situations à risque — en partenariat avec Code2Roues.fr.",
  chapters: [
    {
      slug: 'je-m-equipe',
      num: 'Chapitre 1',
      title: "Je m'équipe pour me protéger",
      objectif: 'Savoir choisir et vérifier son équipement avant une séance.',
      image: IMG.equip,
      sections: [
        {
          title: "1.1. Pourquoi l'équipement est-il essentiel ?",
          blocks: [
            { t: 'p', text: "À deux-roues, mon corps est directement exposé à l'environnement. En cas de chute, il peut entrer en contact avec la chaussée ou un obstacle." },
            { t: 'p', text: "L'équipement permet de limiter certaines blessures. Il contribue aussi à mon confort et à ma visibilité." },
            { t: 'p', text: "Il ne supprime jamais le risque d'accident. La meilleure protection reste une conduite adaptée, une bonne observation et le respect des règles de sécurité." },
          ],
        },
        {
          title: '1.2. Les équipements à connaître',
          blocks: [
            { t: 'sub', text: 'Le casque' },
            { t: 'p', text: "Il protège la tête en cas de choc. Il doit être homologué, adapté à ma morphologie, en bon état et correctement attaché. Un casque trop grand ou mal fermé peut compromettre sa protection." },
            { t: 'sub', text: 'Les gants' },
            { t: 'p', text: "Les mains sont souvent exposées lors d'une chute. Les gants adaptés protègent les mains et doivent permettre de saisir correctement les commandes." },
            { t: 'sub', text: 'Les vêtements et les chaussures' },
            { t: 'p', text: "Une veste à manches longues, un pantalon et des chaussures montantes adaptées protègent davantage que des vêtements légers ou des chaussures ouvertes. Les vêtements doivent permettre de bouger sans gêner la conduite." },
            { t: 'imggrid', items: [
              { src: '/assets/mpd/equip-casque.jpg', label: 'Casque homologué' },
              { src: '/assets/mpd/equip-gants.jpg', label: 'Gants adaptés' },
              { src: '/assets/mpd/equip-blouson.jpg', label: 'Blouson à manches longues' },
              { src: '/assets/mpd/equip-pantalon.jpg', label: 'Pantalon renforcé' },
              { src: '/assets/mpd/equip-bottes.jpg', label: 'Chaussures montantes' },
            ] },
            { t: 'callout', kind: 'attention', text: "Pour la formation pratique à la catégorie AM, option cyclomoteur, les équipements exigés sont définis par la réglementation. Un équipement non conforme peut empêcher la réalisation de la séance." },
          ],
        },
        {
          title: '1.3. Je vérifie mon équipement',
          blocks: [
            { t: 'p', text: 'Avant de commencer, je vérifie :' },
            { t: 'list', items: ['Mon casque est adapté et en bon état.', 'La jugulaire est correctement fermée.', 'Mes gants sont adaptés et bien ajustés.', 'Ma veste et mon pantalon couvrent correctement le corps.', 'Mes chaussures maintiennent les pieds et les chevilles.', 'Aucun vêtement ou accessoire ne gêne les commandes.', 'Je peux tourner la tête et bouger les bras librement.'] },
            { t: 'callout', kind: 'retenir', text: "Si mon équipement ne respecte pas les exigences de la formation, je ne commence pas la séance." },
          ],
        },
      ],
    },
    {
      slug: 'je-decouvre-les-commandes',
      num: 'Chapitre 2',
      title: 'Je découvre les commandes',
      objectif: 'Identifier les commandes avant toute mise en mouvement.',
      image: IMG.commandes,
      sections: [
        {
          title: '2.1. Je prends le temps de découvrir mon véhicule',
          blocks: [
            { t: 'p', text: "Tous les deux-roues ne fonctionnent pas exactement de la même manière. Un scooter automatique ne possède pas les mêmes commandes qu'une moto à boîte de vitesses. La position des commandes peut également varier selon les modèles." },
            { t: 'p', text: "Avant de démarrer, je demande à mon enseignant de me montrer les commandes du véhicule utilisé." },
          ],
        },
        {
          title: "2.2. Les commandes principales d'un scooter automatique",
          blocks: [
            { t: 'sub', text: "La poignée d'accélération" },
            { t: 'p', text: "Elle permet de commander l'accélération. Je dois apprendre à la manipuler progressivement et sans mouvement brusque." },
            { t: 'sub', text: 'Le levier de frein' },
            { t: 'p', text: "Sur de nombreux scooters, un levier commande le frein arrière et l'autre le frein avant. Je dois vérifier la disposition sur le véhicule utilisé, sans la supposer." },
            { t: 'sub', text: "Les clignotants et l'avertisseur sonore" },
            { t: 'p', text: "Les clignotants signalent mon intention de changer de direction. L'avertisseur sonore sert à avertir lorsque cela est nécessaire. Aucun de ces dispositifs ne remplace l'observation." },
            { t: 'imggrid', items: [
              { src: '/assets/mpd/cmd-commodo-gauche.jpg', label: 'Commodo gauche : clignotants, avertisseur, feux' },
              { src: '/assets/mpd/cmd-compteur.jpg', label: 'Tableau de bord et témoins' },
              { src: '/assets/mpd/cmd-commodo-droit.jpg', label: "Poignée d'accélération et levier de frein" },
              { src: '/assets/mpd/cmd-feu-arriere.jpg', label: 'Feu stop et clignotants arrière' },
            ] },
          ],
        },
        {
          title: "2.3. Les commandes d'une moto à boîte de vitesses",
          blocks: [
            { t: 'p', text: 'Sur une moto à boîte de vitesses, certaines commandes supplémentaires sont présentes :' },
            { t: 'list', items: ['Le sélecteur de vitesses : permet de sélectionner les rapports.', "Le levier d'embrayage : permet de gérer la liaison entre le moteur et la transmission.", "La poignée d'accélération : commande l'accélération.", "Les freins : permettent de ralentir et d'arrêter le véhicule."] },
            { t: 'p', text: "Le fonctionnement exact et la disposition des commandes doivent être expliqués par l'enseignant sur la moto utilisée." },
            { t: 'callout', kind: 'attention', text: "Les techniques de démarrage, de changement de rapport et de freinage sont apprises lors d'exercices encadrés. Le livret sert à comprendre les commandes, pas à remplacer l'apprentissage pratique." },
          ],
        },
        {
          title: '2.4. Les commandes ne sont pas toutes identiques',
          blocks: [
            { t: 'p', text: "Je ne dois jamais monter sur un autre deux-roues en supposant que les commandes sont identiques à celles du précédent. Je repère :" },
            { t: 'list', items: ["la commande d'accélération", 'les commandes de freinage', 'les clignotants', 'les feux', "l'avertisseur sonore", 'les témoins et informations du tableau de bord', 'les éventuelles commandes propres au modèle'] },
          ],
        },
      ],
    },
    {
      slug: 'je-controle-avant-le-depart',
      num: 'Chapitre 3',
      title: 'Je contrôle le véhicule avant le départ',
      objectif: "Repérer une anomalie apparente et savoir quand demander une vérification.",
      image: IMG.controle,
      sections: [
        {
          title: '3.1. Les pneus',
          blocks: [
            { t: 'p', text: 'Les pneus assurent le contact entre le véhicule et la chaussée. Je regarde si :' },
            { t: 'list', items: ['le pneu semble suffisamment gonflé', 'une coupure, une déformation ou une hernie est visible', 'un objet est planté dans le pneu', "l'usure paraît anormale"] },
            { t: 'p', text: "Une inspection visuelle ne permet pas de confirmer la pression exacte. Celle-ci doit être mesurée selon les préconisations du constructeur." },
            { t: 'imggrid', items: [
              { src: '/assets/mpd/ctrl-frein-roue.jpg', label: 'Pneu et frein à disque avant' },
              { src: '/assets/mpd/ctrl-moteur.jpg', label: 'Transmission et béquille' },
              { src: '/assets/mpd/ctrl-coffre.jpg', label: 'Rangement sous la selle' },
              { src: '/assets/mpd/ctrl-reservoir.jpg', label: 'Bouchon de réservoir' },
            ] },
          ],
        },
        {
          title: '3.2. Les freins',
          blocks: [
            { t: 'p', text: "Je repère les commandes et j'observe leur état apparent. Je signale toute anomalie : commande endommagée, fuite visible, témoin d'alerte ou sensation anormale lors d'un contrôle réalisé dans les conditions prévues." },
            { t: 'callout', kind: 'attention', text: 'Je ne prends pas la route si le freinage semble défaillant.' },
          ],
        },
        {
          title: '3.3. Les feux et les clignotants',
          blocks: [
            { t: 'p', text: "Je vérifie l'état apparent des feux et des dispositifs de signalisation, puis leur fonctionnement selon la procédure adaptée au véhicule. Je dois pouvoir signaler mes intentions et être visible par les autres usagers." },
          ],
        },
        {
          title: '3.4. Les rétroviseurs',
          blocks: [
            { t: 'p', text: "Je vérifie qu'ils sont présents, propres, bien fixés et correctement réglés. Ils me permettent d'observer une partie de l'environnement derrière moi. Ils ne montrent toutefois pas tout ce qui se trouve autour du véhicule." },
          ],
        },
        {
          title: '3.5. Le tableau de bord',
          blocks: [
            { t: 'p', text: "Je repère les informations essentielles et les éventuels témoins d'alerte. Un voyant peut signaler un fonctionnement normal, une fonction activée ou une anomalie. Sa signification dépend du véhicule." },
            { t: 'p', text: "Je consulte le manuel et demande une explication si je ne comprends pas un témoin." },
          ],
        },
      ],
      checklist: {
        title: 'Avant de partir',
        goal: 'Cochez chaque étape pour tester la checklist. Cette démonstration ne remplace pas le contrôle réel du véhicule.',
        items: [
          'Équipement vérifié',
          'État apparent des pneus vérifié',
          'Commandes et freins repérés',
          'Feux et clignotants contrôlés',
          'Rétroviseurs vérifiés',
          'Tableau de bord observé',
          'Anomalies éventuelles signalées',
        ],
      },
    },
    {
      slug: 'equilibre-et-regard',
      num: 'Chapitre 4',
      title: "Je découvre l'équilibre et le regard",
      objectif: 'Comprendre les bases de la maîtrise du deux-roues.',
      image: IMG.equilibre,
      sections: [
        {
          title: "4.1. Pourquoi l'équilibre est-il important ?",
          blocks: [
            { t: 'p', text: "Un deux-roues demande au conducteur de gérer son équilibre tout en contrôlant sa direction, sa vitesse et son freinage." },
            { t: 'p', text: "À l'arrêt et à très faible allure, l'équilibre ne se gère pas exactement comme lorsque le véhicule roule. La position du corps, les mouvements et le regard influencent la maîtrise du véhicule." },
            { t: 'p', text: "Je ne cherche pas à reproduire seul des exercices vus sur Internet. Je découvre ces sensations progressivement avec mon enseignant, dans un espace adapté." },
            { t: 'imggrid', items: [
              { src: '/assets/mpd/plateau-slalom.jpg', label: 'Slalom entre les cônes, à allure réduite' },
              { src: '/assets/mpd/plateau-depart.jpg', label: 'Départ en ligne droite, regard loin devant' },
              { src: '/assets/mpd/plateau-assis.jpg', label: 'Position assise et prise en main' },
              { src: '/assets/mpd/plateau-cones.jpg', label: 'Trajectoire guidée sur piste' },
            ] },
          ],
        },
        {
          title: "4.2. Le regard guide l'observation",
          blocks: [
            { t: 'p', text: "Le regard ne doit pas rester fixé sur la roue avant ou sur un obstacle immédiat. Je dois apprendre à observer la zone où je souhaite me diriger, tout en surveillant les éléments qui peuvent affecter ma sécurité." },
            { t: 'p', text: "Regarder suffisamment loin me permet de mieux anticiper les changements de direction, les obstacles et les situations qui évoluent." },
            { t: 'callout', kind: 'savais', title: 'Exemple', text: "Si je fixe uniquement un obstacle, je risque de ne pas percevoir les autres informations utiles autour de moi. L'enseignant m'apprend à organiser mon regard en fonction de la situation." },
          ],
        },
        {
          title: '4.3. La position du corps',
          blocks: [
            { t: 'p', text: "Une position adaptée me permet d'accéder aux commandes et de garder une maîtrise confortable du véhicule. Je dois éviter :" },
            { t: 'list', items: ['de me crisper inutilement', 'de verrouiller les bras', 'de regarder constamment le sol', 'de modifier brutalement ma position', 'de laisser mes pieds ou mes vêtements gêner les commandes'] },
            { t: 'p', text: "La position précise dépend du véhicule et de l'exercice." },
          ],
        },
        {
          title: '4.4. Je comprends la trajectoire',
          blocks: [
            { t: 'p', text: "Une trajectoire doit être adaptée à la route, à la visibilité, à l'adhérence et aux autres usagers." },
            { t: 'p', text: "Je ne dois pas chercher à prendre un virage rapidement. Je dois comprendre où regarder, comment conserver une marge de sécurité et comment éviter de me rapprocher dangereusement des obstacles ou des usagers." },
            { t: 'p', text: "Les techniques de trajectoire sont abordées en pratique avec l'enseignant, en fonction du niveau de l'élève." },
          ],
        },
        {
          title: '4.5. Je comprends le freinage',
          blocks: [
            { t: 'p', text: "Freiner demande d'anticiper, d'observer et de maîtriser les commandes. Un freinage brutal ou mal adapté peut déséquilibrer le véhicule, notamment lorsque l'adhérence est réduite." },
            { t: 'p', text: "Je dois apprendre à identifier les commandes, comprendre leur rôle et appliquer les consignes données par mon enseignant. Je ne teste pas les limites du freinage sur route ouverte." },
            { t: 'callout', kind: 'retenir', text: "Équilibre, regard, trajectoire et freinage sont liés. Ils s'apprennent progressivement et ne peuvent pas être maîtrisés par la seule lecture d'un livret." },
          ],
        },
      ],
    },
    {
      slug: 'situations-a-risque',
      num: 'Chapitre 5',
      title: 'Je comprends les situations à risque',
      objectif: "Reconnaître les dangers avant qu'ils ne deviennent urgents.",
      image: IMG.risques,
      sections: [
        {
          title: '5.1. Une chaussée humide',
          blocks: [
            { t: 'p', text: "La pluie peut réduire l'adhérence et la visibilité. Les marquages au sol, les plaques métalliques et certaines surfaces peuvent devenir plus glissants." },
            { t: 'p', text: 'Je dois adapter mon allure, augmenter ma prudence et éviter les gestes brusques.' },
          ],
        },
        {
          title: '5.2. Un véhicule qui tourne',
          blocks: [
            { t: 'p', text: "Un automobiliste peut ne pas m'avoir vu, notamment dans un angle mort ou derrière un obstacle. Je ne considère jamais que l'autre usager m'a forcément repéré." },
            { t: 'p', text: "J'observe ses mouvements, j'anticipe une éventuelle manœuvre et je conserve une marge de sécurité." },
          ],
        },
        {
          title: '5.3. Un obstacle sur la chaussée',
          blocks: [
            { t: 'p', text: "Un trou, du gravier, un objet ou une zone dégradée peuvent perturber la stabilité. Je dois observer suffisamment tôt pour identifier le danger et adapter mon comportement sans réaction précipitée." },
            { t: 'p', text: "La manœuvre à effectuer dépend de la situation réelle et de la présence d'autres usagers." },
          ],
        },
        {
          title: '5.4. La pression du groupe',
          blocks: [
            { t: 'p', text: "Un ami peut m'encourager à aller plus vite, à réaliser une manœuvre risquée ou à ne pas respecter une règle. Je dois être capable de refuser." },
            { t: 'p', text: "Ma sécurité ne dépend pas de la capacité à impressionner les autres, mais de ma capacité à prendre des décisions adaptées." },
          ],
        },
        {
          title: '5.5. Les distractions',
          blocks: [
            { t: 'p', text: "Le téléphone, les écouteurs qui isolent des sons utiles, une conversation ou la recherche d'une commande peuvent détourner mon attention." },
            { t: 'p', text: "Avant de partir, je prépare ce dont j'ai besoin. Pendant la conduite, je reste concentré sur mon environnement et sur la maîtrise du véhicule." },
          ],
        },
      ],
      quiz: {
        title: 'Quiz interactif — Vérifier mes connaissances',
        questions: [
          { q: 'À quoi sert principalement le casque ?', options: ['À éviter toute blessure', 'À protéger la tête en cas de choc', 'À améliorer la visibilité uniquement'], answer: 1, explain: "Le casque protège la tête en cas de choc, mais ne supprime pas tous les risques de blessure." },
          { q: 'Que faire si je découvre une anomalie importante sur le freinage ?', options: ['Partir doucement', 'Demander une vérification avant de rouler', 'Tester les freins en circulation'], answer: 1, explain: 'Un freinage défaillant doit être vérifié avant toute prise de route.' },
          { q: 'Les clignotants donnent-ils la priorité ?', options: ["Oui, dès qu'ils sont activés", 'Non, ils signalent une intention', 'Seulement en agglomération'], answer: 1, explain: 'Les clignotants signalent une intention mais ne donnent jamais la priorité.' },
          { q: 'Pourquoi regarder au-delà de la roue avant ?', options: ["Pour mieux anticiper l'évolution de la situation", 'Pour rouler plus vite', "Pour éviter d'utiliser les rétroviseurs"], answer: 0, explain: "Regarder loin permet d'anticiper les changements de direction et les obstacles." },
          { q: 'Que faire si les commandes diffèrent de celles du véhicule habituel ?', options: ['Les deviner en roulant', 'Demander une explication avant de démarrer', 'Ignorer les commandes inconnues'], answer: 1, explain: 'Les commandes doivent être comprises avant toute mise en mouvement.' },
          { q: 'Un ami me pousse à essayer une manœuvre risquée. Quelle est la bonne réaction ?', options: ['Accepter pour montrer mon niveau', 'Refuser si je ne suis pas prêt et demander conseil', 'Essayer sans prévenir'], answer: 1, explain: 'Savoir refuser fait partie des décisions qui protègent le conducteur.' },
        ],
      },
      bilan: {
        title: 'Bilan du module',
        intro: 'À la fin du dossier, je dois pouvoir expliquer :',
        items: [
          'le rôle des principaux équipements de protection',
          'le fonctionnement des commandes de mon véhicule',
          'les vérifications à réaliser avant une séance',
          "l'importance du regard et de l'équilibre",
          "pourquoi le freinage et les trajectoires s'apprennent progressivement",
          'comment identifier et anticiper les situations à risque',
        ],
        next: "Le bilan peut être complété avec l'enseignant lors de la séance pratique.",
      },
    },
  ],
};
